/**
 * Admin Shared Charts Page
 * Analytics for shared charts with view statistics
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  Input,
  Select,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  useToast,
  Spinner,
  HStack,
  VStack,
  Grid,
  GridItem,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Link,
  Code,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { Search, RefreshCw, Eye, Link as LinkIcon, Copy, CheckCircle, ExternalLink } from 'lucide-react';
import {
  adminService,
  type SharedChartActivity,
  type GetChartsParams,
  type SharedChartsStatsResponse,
  type SharedChartActivityDetailResponse,
} from '../../services/AdminService';
import { formatDistanceToNow, format } from 'date-fns';

export const SharedChartsPage: React.FC = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // State
  const [charts, setCharts] = useState<SharedChartActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<SharedChartsStatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 25,
    pages: 1,
  });

  // Chart detail modal
  const [selectedChartId, setSelectedChartId] = useState<number | null>(null);
  const [chartDetail, setChartDetail] = useState<SharedChartActivityDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'last_view' | 'total_views' | 'created_at'>('last_view');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Load charts stats
  const loadStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const response = await adminService.getSharedChartsStats();
      setStats(response);
    } catch (error: any) {
      toast({
        title: 'Error loading charts stats',
        description: error.response?.data?.detail || 'Failed to load stats',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setStatsLoading(false);
    }
  }, [toast]);

  // Load charts
  const loadCharts = useCallback(async (params?: Partial<GetChartsParams>) => {
    try {
      setLoading(true);
      const response = await adminService.getSharedChartsActivity({
        search: searchQuery || undefined,
        sort: sortBy,
        order: sortOrder,
        page: pagination.page,
        limit: pagination.limit,
        ...params,
      });

      setCharts(response.charts);
      setPagination(response.pagination);
    } catch (error: any) {
      toast({
        title: 'Error loading shared charts',
        description: error.response?.data?.detail || 'Failed to load charts',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, sortBy, sortOrder, pagination.page, pagination.limit, toast]);

  // Load chart detail
  const loadChartDetail = useCallback(async (chartId: number) => {
    try {
      setDetailLoading(true);
      const response = await adminService.getSharedChartActivityDetail(chartId, 30);
      setChartDetail(response);
    } catch (error: any) {
      toast({
        title: 'Error loading chart activity',
        description: error.response?.data?.detail || 'Failed to load activity',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDetailLoading(false);
    }
  }, [toast]);

  // Initial load
  useEffect(() => {
    loadStats();
    loadCharts();
  }, []);

  // Handle search
  const handleSearch = () => {
    loadCharts({ page: 1 });
  };

  // Handle sort change
  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const [sort, order] = e.target.value.split('-') as [typeof sortBy, typeof sortOrder];
    setSortBy(sort);
    setSortOrder(order);
    loadCharts({ page: 1, sort, order });
  };

  // Handle refresh
  const handleRefresh = () => {
    loadStats();
    loadCharts({ page: pagination.page });
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setPagination({ ...pagination, page: newPage });
    loadCharts({ page: newPage });
  };

  // Handle row click
  const handleRowClick = (chartId: number) => {
    setSelectedChartId(chartId);
    loadChartDetail(chartId);
    onOpen();
  };

  // Copy short code
  const handleCopyCode = async (code: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/s/${code}`);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
      toast({
        title: 'Copied',
        description: 'Link copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy link',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Format relative time
  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return 'Never';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'N/A';
    }
  };

  // Check if expired
  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="flex-end" align="center">
          <Button
            leftIcon={<RefreshCw size={16} />}
            onClick={handleRefresh}
            size="sm"
            variant="outline"
          >
            Refresh
          </Button>
        </Flex>

        {/* Stats Cards */}
        {statsLoading ? (
          <Flex justify="center" py={8}>
            <Spinner size="lg" color="blue.500" />
          </Flex>
        ) : stats && (
          <>
            {/* Charts Stats */}
            <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
              <GridItem>
                <Box bg="white" p={6} borderRadius="md" shadow="sm">
                  <HStack spacing={3} mb={3}>
                    <Box p={2} bg="blue.100" borderRadius="md">
                      <LinkIcon size={20} color="#3182CE" />
                    </Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">
                      Total Charts
                    </Text>
                  </HStack>
                  <Text fontSize="3xl" fontWeight="bold">
                    {stats.total_charts}
                  </Text>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg="white" p={6} borderRadius="md" shadow="sm">
                  <Stat>
                    <StatLabel>Created Today</StatLabel>
                    <StatNumber color="green.500">{stats.charts_today}</StatNumber>
                    <StatHelpText>New shared links</StatHelpText>
                  </Stat>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg="white" p={6} borderRadius="md" shadow="sm">
                  <Stat>
                    <StatLabel>This Week</StatLabel>
                    <StatNumber>{stats.charts_this_week}</StatNumber>
                    <StatHelpText>Last 7 days</StatHelpText>
                  </Stat>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg="white" p={6} borderRadius="md" shadow="sm">
                  <Stat>
                    <StatLabel>This Month</StatLabel>
                    <StatNumber>{stats.charts_this_month}</StatNumber>
                    <StatHelpText>Last 30 days</StatHelpText>
                  </Stat>
                </Box>
              </GridItem>
            </Grid>

            {/* Views Stats */}
            <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
              <GridItem>
                <Box bg="white" p={6} borderRadius="md" shadow="sm">
                  <HStack spacing={3} mb={3}>
                    <Box p={2} bg="green.100" borderRadius="md">
                      <Eye size={20} color="#38A169" />
                    </Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">
                      Views Today
                    </Text>
                  </HStack>
                  <Text fontSize="3xl" fontWeight="bold" color="green.500">
                    {stats.total_views_today.toLocaleString()}
                  </Text>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg="white" p={6} borderRadius="md" shadow="sm">
                  <Stat>
                    <StatLabel>Views This Week</StatLabel>
                    <StatNumber>{stats.total_views_week.toLocaleString()}</StatNumber>
                    <StatHelpText>Last 7 days</StatHelpText>
                  </Stat>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg="white" p={6} borderRadius="md" shadow="sm">
                  <Stat>
                    <StatLabel>Views This Month</StatLabel>
                    <StatNumber>{stats.total_views_month.toLocaleString()}</StatNumber>
                    <StatHelpText>Last 30 days</StatHelpText>
                  </Stat>
                </Box>
              </GridItem>
            </Grid>
          </>
        )}

        {/* Filters */}
        <Box bg="white" p={6} borderRadius="md" shadow="sm">
          <HStack spacing={4}>
            <InputGroup flex={2}>
              <InputLeftElement pointerEvents="none">
                <Search size={16} color="gray" />
              </InputLeftElement>
              <Input
                placeholder="Search by short code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </InputGroup>

            <Select
              value={`${sortBy}-${sortOrder}`}
              onChange={handleSortChange}
              flex={1}
              maxW="250px"
            >
              <option value="last_view-desc">Last View (Recent)</option>
              <option value="last_view-asc">Last View (Oldest)</option>
              <option value="total_views-desc">Total Views (High)</option>
              <option value="total_views-asc">Total Views (Low)</option>
              <option value="created_at-desc">Created (Newest)</option>
              <option value="created_at-asc">Created (Oldest)</option>
            </Select>

            <Button colorScheme="blue" onClick={handleSearch}>
              Search
            </Button>
          </HStack>
        </Box>

        {/* Charts Table */}
        <Box bg="white" borderRadius="md" shadow="sm" overflow="hidden">
          {loading ? (
            <Flex justify="center" align="center" py={20}>
              <Spinner size="xl" color="blue.500" />
            </Flex>
          ) : charts.length === 0 ? (
            <Flex justify="center" align="center" py={20}>
              <Text fontSize="lg" color="gray.500">
                No shared charts found
              </Text>
            </Flex>
          ) : (
            <>
              <Table variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Code</Th>
                    <Th>Name</Th>
                    <Th>Owner</Th>
                    <Th>Status</Th>
                    <Th isNumeric>Total Views</Th>
                    <Th isNumeric>Today</Th>
                    <Th>Last View</Th>
                    <Th>Created</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {charts.map((chart) => (
                    <Tr
                      key={chart.id}
                      _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                      onClick={() => handleRowClick(chart.id)}
                      opacity={isExpired(chart.expires_at) ? 0.6 : 1}
                    >
                      <Td>
                        <HStack spacing={2}>
                          <Code fontSize="sm" colorScheme="blue">
                            {chart.short_code}
                          </Code>
                          <Tooltip label={copiedCode === chart.short_code ? 'Copied!' : 'Copy link'}>
                            <IconButton
                              aria-label="Copy link"
                              icon={copiedCode === chart.short_code ? <CheckCircle size={14} /> : <Copy size={14} />}
                              size="xs"
                              variant="ghost"
                              onClick={(e) => handleCopyCode(chart.short_code, e)}
                              colorScheme={copiedCode === chart.short_code ? 'green' : 'gray'}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                      <Td>
                        <Text fontSize="sm" noOfLines={1} maxW="150px">
                          {chart.chart_name || '—'}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="gray.600">
                          {chart.owner_username || 'Anonymous'}
                        </Text>
                      </Td>
                      <Td>
                        {isExpired(chart.expires_at) ? (
                          <Badge colorScheme="red">Expired</Badge>
                        ) : chart.expires_at ? (
                          <Badge colorScheme="yellow">Expires {formatRelativeTime(chart.expires_at)}</Badge>
                        ) : (
                          <Badge colorScheme="green">Active</Badge>
                        )}
                      </Td>
                      <Td isNumeric>
                        <Text fontWeight="medium">{chart.total_views.toLocaleString()}</Text>
                      </Td>
                      <Td isNumeric>
                        <Text fontWeight="medium" color={chart.views_today > 0 ? 'green.500' : 'gray.400'}>
                          {chart.views_today}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="gray.600">
                          {formatRelativeTime(chart.last_view)}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="gray.600">
                          {format(new Date(chart.created_at), 'MMM dd, yyyy')}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <Flex justify="space-between" align="center" p={4} borderTop="1px" borderColor="gray.200">
                  <Text fontSize="sm" color="gray.600">
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} charts
                  </Text>
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      isDisabled={pagination.page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      isDisabled={pagination.page === pagination.pages}
                    >
                      Next
                    </Button>
                  </HStack>
                </Flex>
              )}
            </>
          )}
        </Box>

        {/* Chart Detail Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Chart View Statistics</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              {detailLoading ? (
                <Flex justify="center" py={8}>
                  <Spinner size="lg" color="blue.500" />
                </Flex>
              ) : chartDetail ? (
                <VStack spacing={4} align="stretch">
                  {/* Chart Info */}
                  <Box p={4} bg="gray.50" borderRadius="md">
                    <HStack justify="space-between" align="start">
                      <VStack align="start" spacing={1}>
                        <Code fontSize="lg" colorScheme="blue">{chartDetail.short_code}</Code>
                        {chartDetail.chart_name && (
                          <Text fontWeight="medium">{chartDetail.chart_name}</Text>
                        )}
                        <Text fontSize="sm" color="gray.600">
                          Owner: {chartDetail.owner_username || 'Anonymous'}
                        </Text>
                      </VStack>
                      <Link
                        href={`/s/${chartDetail.short_code}`}
                        isExternal
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button size="sm" leftIcon={<ExternalLink size={14} />} variant="outline">
                          View Chart
                        </Button>
                      </Link>
                    </HStack>
                  </Box>

                  {/* Summary Stats */}
                  <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                    <Box p={4} bg="blue.50" borderRadius="md" textAlign="center">
                      <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                        {chartDetail.total_views.toLocaleString()}
                      </Text>
                      <Text fontSize="sm" color="gray.600">Total Views (30d)</Text>
                    </Box>
                    <Box p={4} bg="green.50" borderRadius="md" textAlign="center">
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">First View</Text>
                      <Text fontSize="sm" color="green.600">
                        {chartDetail.first_view
                          ? format(new Date(chartDetail.first_view), 'MMM dd, yyyy')
                          : 'No views yet'}
                      </Text>
                    </Box>
                    <Box p={4} bg="purple.50" borderRadius="md" textAlign="center">
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">Last View</Text>
                      <Text fontSize="sm" color="purple.600">
                        {formatRelativeTime(chartDetail.last_view)}
                      </Text>
                    </Box>
                  </Grid>

                  {/* Daily Stats Table */}
                  <Box>
                    <Text fontWeight="medium" mb={2}>Daily Views (Last 30 Days)</Text>
                    <Box maxH="300px" overflowY="auto" border="1px" borderColor="gray.200" borderRadius="md">
                      <Table size="sm">
                        <Thead bg="gray.50" position="sticky" top={0}>
                          <Tr>
                            <Th>Date</Th>
                            <Th isNumeric>Views</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {chartDetail.daily_stats.length > 0 ? (
                            chartDetail.daily_stats.map((stat) => (
                              <Tr key={stat.date}>
                                <Td>{format(new Date(stat.date), 'MMM dd, yyyy')}</Td>
                                <Td isNumeric fontWeight="medium">{stat.view_count}</Td>
                              </Tr>
                            ))
                          ) : (
                            <Tr>
                              <Td colSpan={2} textAlign="center" color="gray.500">
                                No views in the last 30 days
                              </Td>
                            </Tr>
                          )}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                </VStack>
              ) : (
                <Text color="gray.500">No data available</Text>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
};

export default SharedChartsPage;
